import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Avatar,
} from '@mui/material';
import { 
  Person,
  Edit,
  Save,
  Cancel,
  Email,
  LocationOn,
  Payment,
  CalendarToday,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { userAPI } from '../services/apiService';

const ProfilePage: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    employmentState: user?.employmentState || '',
  });

  const US_STATES = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
    'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
    'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
    'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
    'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
    'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
    'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
    'Wisconsin', 'Wyoming'
  ];

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        employmentState: user.employmentState || '',
      });
    }
  }, [user]);

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      const updates = {
        name: formData.name !== user?.name ? formData.name : undefined,
        employmentState: formData.employmentState !== user?.employmentState ? formData.employmentState : undefined,
      };

      // Only send updates for changed fields
      const hasChanges = Object.values(updates).some(value => value !== undefined);
      
      if (hasChanges) {
        await userAPI.updateProfile(updates);
        
        // Update local user data
        updateUser({
          ...user!,
          name: formData.name,
          employmentState: formData.employmentState,
        });
        
        setSuccess('Profile updated successfully!');
      }
      
      setEditing(false);
    } catch (err) {
      setError('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: user?.name || '',
      employmentState: user?.employmentState || '',
    });
    setEditing(false);
    setError(null);
  };

  const formatServiceType = (serviceType: string) => {
    return serviceType === 'fmla' ? 'FMLA Certification' : 'FMLA + STD Certification';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part.charAt(0).toUpperCase())
      .join('')
      .slice(0, 2);
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          My Profile
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Manage your account information and preferences
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Profile Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Typography variant="h6">
              Personal Information
            </Typography>
            {!editing ? (
              <Button 
                startIcon={<Edit />} 
                onClick={() => setEditing(true)}
                variant="outlined"
              >
                Edit Profile
              </Button>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  startIcon={<Save />} 
                  onClick={handleSave}
                  variant="contained"
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={20} /> : 'Save'}
                </Button>
                <Button 
                  startIcon={<Cancel />} 
                  onClick={handleCancel}
                  variant="outlined"
                >
                  Cancel
                </Button>
              </Box>
            )}
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
              <Avatar 
                sx={{ 
                  width: 100, 
                  height: 100, 
                  mx: 'auto', 
                  mb: 2,
                  bgcolor: 'primary.main',
                  fontSize: '2rem'
                }}
              >
                {user?.name ? getInitials(user.name) : <Person />}
              </Avatar>
              <Typography variant="h6">
                {user?.name || 'Patient'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                FMLA Patient
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={8}>
              {editing ? (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel>Employment State</InputLabel>
                      <Select
                        value={formData.employmentState}
                        label="Employment State"
                        onChange={(e) => setFormData(prev => ({ ...prev, employmentState: e.target.value }))}
                      >
                        {US_STATES.map((state) => (
                          <MenuItem key={state} value={state}>
                            {state}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Person sx={{ mr: 1, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Full Name
                        </Typography>
                        <Typography variant="body1">
                          {user?.name || 'Not provided'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Email sx={{ mr: 1, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Email Address
                        </Typography>
                        <Typography variant="body1">
                          {user?.email}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <LocationOn sx={{ mr: 1, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Employment State
                        </Typography>
                        <Typography variant="body1">
                          {user?.employmentState || 'Not specified'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Service Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Service Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Payment sx={{ mr: 1, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Service Type
                  </Typography>
                  <Typography variant="body1">
                    {user?.serviceType ? formatServiceType(user.serviceType) : 'Not specified'}
                  </Typography>
                  <Chip 
                    label={`$${user?.paymentAmount || 100}`} 
                    color="primary" 
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CalendarToday sx={{ mr: 1, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Registration Date
                  </Typography>
                  <Typography variant="body1">
                    {user?.registrationDate ? formatDate(user.registrationDate) : 'Unknown'}
                  </Typography>
                  {user?.paymentDate && (
                    <Typography variant="body2" color="text.secondary">
                      Payment: {formatDate(user.paymentDate)}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Account Settings */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Account Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Manage your account preferences and privacy settings
          </Typography>
          
          <Divider sx={{ my: 2 }} />
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Button 
                variant="outlined" 
                fullWidth
                disabled
                sx={{ mb: 2 }}
              >
                Change Password (Coming Soon)
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button 
                variant="outlined" 
                fullWidth
                disabled
                sx={{ mb: 2 }}
              >
                Privacy Settings (Coming Soon)
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button 
                variant="outlined" 
                fullWidth
                disabled
              >
                Notification Preferences (Coming Soon)
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button 
                variant="outlined" 
                color="error"
                fullWidth
                disabled
              >
                Delete Account (Coming Soon)
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Privacy Notice */}
      <Box sx={{ mt: 4, p: 3, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom>
          Privacy & Security
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Your personal information is protected by HIPAA regulations and our comprehensive privacy policy. 
          We use industry-standard encryption to protect your data and will never share your personal health 
          information without your explicit consent, except as required by law.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          All data is stored securely in HIPAA-compliant infrastructure and is retained for 7 years as 
          required by medical record retention laws.
        </Typography>
      </Box>
    </Container>
  );
};

export default ProfilePage;